// @flow
import * as React from 'react';
import styled from 'styled-components';
import type { FormBuilderBlockData, FormBuilderField } from 'api/cms';
import { imageUrl } from 'utils/filestack';
import { trackEvent } from 'utils/analytics';
import { RichTextHeadline, BodyLarge } from 'components/Text';
import Input from 'components/Input';
import SelectInput from 'components/SelectInput';
import Checkbox from 'components/Checkbox';
import Box from 'components/Box';
import { Label } from 'components/Form/Field';
import { PrimaryButton } from 'components/Button';
import TabContext from 'containers/TabContext';
import { colors } from '../../theme';

const { useContext, useState, useEffect, useCallback } = React;

const getImageUrl = (handle: string) =>
  imageUrl({
    handle,
    transform: `resize=width:2096,fit:max/quality=value:70`
  });

export const FormContainer = styled.div`
  ${props =>
    props.$disabled &&
    `
  pointer-events: none;
`}
  && {
    input,
    select {
      border: none !important;
    }
    .checkmark-container {
      box-shadow: none !important;
      background: white !important;
      svg {
        * {
          stroke: ${colors.waterBlue};
        }
      }
    }
    input:not([type='checkbox']),
    select {
      height: 50px;
      ${props =>
        props.$isTabbing
          ? `box-shadow: 0 0 0 6px white inset, -16px 0 0 0 white inset, 
                16px 0 0 0 white inset, 0 -5px ${colors.waterBlue} inset;`
          : ''}
      &:focus {
        box-shadow: ${props =>
          !props.$isTabbing
            ? `0 2px 5px 2px rgba(0, 0, 0, 0.3)`
            : `0 2px 5px 2px rgba(0, 0, 0, 0.3), 
                0 0 0 6px white inset, -16px 0 0 0 white inset, 
                16px 0 0 0 white inset, 0 -8px ${colors.waterBlue} inset`};
      }
    }
    label {
      color: white;
      & + *:not([type='checkbox']) {
        margin-top: 0;
      }
    }
  }
`;

const getId = name => `form-builder-${name}`;

export const Field = ({
  action,
  type,
  name,
  placeholder,
  options,
  required,
  label,
  value: submitValue
}: FormBuilderField & {
  action: string
}) => {
  const [value, setValue] = useState('');
  const [checked, setChecked] = useState(false);
  const [changed, setChanged] = useState(false);

  const id = getId(name);
  const inputProps = { name, placeholder, required, id, 'data-qa-id': id };

  const trackChange = useCallback(
    () => {
      trackEvent('CMS Form Edited', {
        action,
        label
      });
    },
    [action, label]
  );

  useEffect(
    () => {
      if (changed) {
        trackChange();
      }
    },
    [changed, trackChange]
  );

  const setTextValue = e => {
    setValue(e);
    setChanged(true);
  };

  const setSelectValue = e => {
    setValue(e);
    trackChange();
  };

  const updateChecked = e => {
    setChecked(e);
    trackChange();
  };

  switch (type) {
    case 'SELECT':
      return (
        <SelectInput
          options={[
            {
              value: '',
              label: placeholder || getText('Select One'),
              disabled: true
            }
          ].concat(options || [])}
          onChange={setSelectValue}
          value={value}
          {...inputProps}
        />
      );
    case 'CHECKBOX':
      return (
        <Checkbox
          onChange={updateChecked}
          checked={checked}
          value={submitValue}
          label={label}
          large
          {...inputProps}
        />
      );
    case 'HIDDEN':
      return <input name={name} value={submitValue} hidden />;
    case 'TEXT':
    case 'EMAIL':
    default:
      return (
        <Input
          type={type.toLowerCase()}
          changeHandler={setTextValue}
          onFocus={() => setChanged(false)}
          value={value}
          {...inputProps}
        />
      );
  }
};

const StyledLabel = styled(Label)`
  color: white;
  margin-bottom: 6px;
`;

const SubmitButton = styled(
  ({ isSidebar, ...props }: { isSidebar?: boolean }) => (
    <PrimaryButton {...props} />
  )
)`
  padding-left: 40px;
  padding-right: 40px;
  max-width: 440px;
  box-shadow: 0 0 0 1px white inset;
  background: none;
  color: white;
  &:focus,
  &:hover {
    box-shadow: 0 0 0 1px ${colors.turtleGreen} inset;
    background: ${colors.turtleGreen};
  }
  ${props =>
    props.isSidebar &&
    `
    width: 100%;
  `}
`;

const FormBuilderBlock = ({
  data: { headline, submitButtonText, bgImage, action, fields, method }
}: {
  data: FormBuilderBlockData
}) => {
  const isTabbing = useContext(TabContext);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = () => {
    setSubmitted(true);
    trackEvent('CMS Form Submitted', { action });
  };

  return (
    <div>
      <Box
        p="80px 20px"
        width="auto !important"
        borderRadius={[0, 0, 0, 24]}
        background={`url("${
          bgImage
            ? getImageUrl(bgImage.handle)
            : '/static/images/blue-loops.svg'
        }") center/cover`}
      >
        {headline && (
          <Box maxWidth="960px" m="0 auto">
            <RichTextHeadline color="white" textAlign="center">
              {headline}
            </RichTextHeadline>
          </Box>
        )}

        <Box position="relative">
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            display={!submitted && 'none'}
            width={1}
          >
            <BodyLarge color="white" textAlign="center">
              {getText('Thank you!')}
            </BodyLarge>
          </Box>

          <Box visibility={submitted && 'hidden'}>
            <FormContainer $isTabbing={isTabbing}>
              <form
                action={action}
                target="_blank"
                onSubmit={onSubmit}
                method={method}
              >
                <Box maxWidth="440px" m="40px auto 0">
                  {fields.map(field => (
                    <Box
                      my={field.type === 'CHECKBOX' ? 24 : 16}
                      key={field.name}
                    >
                      {!['CHECKBOX', 'HIDDEN'].includes(field.type) && (
                        <StyledLabel htmlFor={getId(field.name)}>
                          {field.label}
                        </StyledLabel>
                      )}
                      <Field {...field} action={action} headline={headline} />
                    </Box>
                  ))}
                  {/* {hiddenFields.map(({ name, value }) => (
                  <input type="hidden" name={name} value={value} key={name} />
                ))} */}
                  <Box classes={['flex', 'justify-center']} mt="40px">
                    <SubmitButton size="large" width={1} type="submit">
                      {submitButtonText}
                    </SubmitButton>
                  </Box>
                </Box>
                <div style={{ position: 'absolute', left: -9999, top: -9999 }}>
                  <label htmlFor="pardot_extra_field">
                    {getText(`Comments (No Use)`)}
                  </label>
                  <input
                    type="text"
                    id="pardot_extra_field"
                    name="pardot_extra_field"
                  />
                </div>
              </form>
            </FormContainer>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default FormBuilderBlock;
